import React, { Suspense } from 'react';
import { InstallSelectLangsHOC } from '../install/SelectLangs/hocs/InstallSelectLangsHOC';
import { theme } from '../../../common/styles/theme';
import { CssBaseline, Stack, ThemeProvider } from '@mui/material';
import { HistoryProrvider } from '../../effects/historyProrvider';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { polyfillCountryFlagEmojis } from 'country-flag-emoji-polyfill';
import {
	DeviceContext,
	IDeviceContext,
} from '../../../common/contexts/DeviceContext';
import { GlobalStyle } from './GlobalStyle';
import { SnackbarPanelHOC } from '../SnackbarPanel/hocs/SnackbarPanelHOC';
import { LoginPopupHOC } from '../install/Login/hocs/LoginPopupHOC/LoginPopupHOC';
import { DeviceDetecor } from '../../../common/utils/deviceDetecor';
import Tappify from 'tappify';
import { UserAuthCallbackHOC } from '../UserAuthCallback/hocs/UserAuthCallbackHOC';
import { CallAppHOC } from '../call/CallApp/hocs/CallAppHOC';
import { AudioPageHOC } from '../call/AudioPage/hocs/AudioPageHOC';

export const App: React.FC = () => {
	const history = HistoryProrvider.getHistory();

	let [deviceContext, _setDeviceContext] = React.useState<IDeviceContext>({});
	const setDeviceContext = (
		value: Partial<IDeviceContext>,
	) => {
		deviceContext = {
			...deviceContext,
			...value,
		};
		_setDeviceContext(deviceContext);
	};

	React.useEffect(() => {

		polyfillCountryFlagEmojis();

		let isMobile!: boolean; // the detect* calls below assign these
		let isSmall: boolean | undefined;
		let isTouch!: boolean;
		let scrollWidth!: number;

		const detectTouch = () => {
			isTouch = DeviceDetecor.isTouch();
			const touchClass = isTouch ? 'touch' : 'no-touch';
			document.documentElement.classList.add(touchClass);
		};
		detectTouch();

		const detectMobile = () => {
			isMobile = DeviceDetecor.isMobile();
			const mobileClass = isMobile ? 'mobile' : 'no-mobile';
			document.documentElement.classList.add(mobileClass);
		};
		detectMobile();

		const detectScroll = () => {
			scrollWidth = DeviceDetecor.scrollWidth();
			const classList = document.documentElement.classList;
			classList.remove('scroll-visible');
			classList.remove('scroll-hidden');
			const scrollClass = scrollWidth ? 'scroll-visible' : 'scroll-hidden';
			classList.add(scrollClass);
		};
		detectScroll();

		const onResize = () => {
			let isSmallNew = DeviceDetecor.isSmall();
			if (isSmall !== isSmallNew) {
				isSmall = isSmallNew;
				setDeviceContext({ isSmall });
			}
		};
		if (!isMobile) {
			onResize();
			window.addEventListener('resize', onResize);
		}

		const setFullScreen = (
			value: boolean,
		) => {
			setDeviceContext({
				isFullScreen: value,
			});
		};

		let w = window as any;
		w.setFullScreen = setFullScreen;

		setDeviceContext({
			isMobile,
			isSmall,
			isTouch,
			scrollWidth,
			setFullScreen,
		});

		setTimeout(() => { // detect scrollWidth after style apply
			detectScroll();
			setDeviceContext({
				scrollWidth,
			});
		}, 100);

		const classList = document.documentElement.classList;
		const unsubscribeTappify = Tappify.subscribe((isCursor: boolean) => {
			if (isCursor !== !isTouch) {
				isTouch = !isCursor;
				setDeviceContext({ isTouch });
				if (isTouch) {
					classList.add('touch');
					classList.remove('no-touch');
				} else {
					classList.remove('touch');
					classList.add('no-touch');
				}
			}
        });

		const handlePopState = (e: Event) => {
			setTimeout(()=>{
				if (window.onpopstates && window.onpopstates.length) {
					window.onpopstate = window.onpopstates.pop()!; // length checked above
				}
			})

		}

		window.addEventListener('popstate', handlePopState);
		return () => {
			window.removeEventListener('popstate', handlePopState);
			window.removeEventListener('resize', onResize);
			unsubscribeTappify();
		}
	}, []);

	// React.useEffect(() => {
	// 	document.addEventListener("click", (e) => {
	// 		const effect = document.createElement("div");
	// 		effect.className = "click-effect";
	// 		effect.style.left = `${e.clientX}px`;
	// 		effect.style.top = `${e.clientY}px`;
	// 		document.body.appendChild(effect);

	// 		effect.addEventListener("animationend", () => {
	// 			effect.remove();
	// 		});
	// 	});
	// }, []);

	return (
		<Router history={history}>
			<Suspense fallback={
				<Stack
					justifyContent={'center'}
					alignItems={'center'}
					sx={{
						height: '100vh',
					}}
				>
					Loading...
				</Stack>
			}>
			<ThemeProvider theme={theme}>
				<CssBaseline />
				{deviceContext.scrollWidth !== undefined &&
					<GlobalStyle />
				}
				<DeviceContext.Provider value={deviceContext}>
					<Stack
						className={`root-inner ${deviceContext.isFullScreen ? 'fullscreen' : ''}`}
						sx={{
							height: 1,
							width: 1,
							pt: '',
							overflow: 'auto',
						}}
					>
						<Switch>
							<Route exact path="/install/selectLangs/:option?">
								<InstallSelectLangsHOC />
							</Route>
							<Route exact path="/authCallback">
								<UserAuthCallbackHOC/>
							</Route>
							<Route exact path="/"></Route>
							<Redirect exact from="/call" to="/" />
							<Route path="/call"></Route>
							{/* The recordings list lives on the home page now */}
							<Redirect exact from="/myStuff/:groupId?" to="/" />
							<Route exact path="/audio/:groupId/:audioId/:tab?">
								<AudioPageHOC />
							</Route>
							{/* /admin is the backend-served admin panel */}
							<Route path="/admin"></Route>
							<Redirect to="/" />
						</Switch>
						<SnackbarPanelHOC />
						<LoginPopupHOC />
						<CallAppHOC />
					</Stack>
				</DeviceContext.Provider>
			</ThemeProvider>
			</Suspense>
			<Tappify.Finger />
			<Tappify.Cursor />
		</Router>
	);
};
